import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { VanchiState } from '../../types';
import { vanchiService } from '../../services/vanchiService';

// Initial state
const initialState: VanchiState = {
  list: [],
  total: 0,
  stats: {
    today: { packedSent: 0, received: 0 },
    yesterday: { packedSent: 0, received: 0 },
    week: { packedSent: 0, received: 0 },
    month: { packedSent: 0, received: 0 },
    year: { packedSent: 0, received: 0 },
  },
  loading: false,
  error: null,
};

// Async thunks
export const fetchVanchiList = createAsyncThunk(
  'vanchi/fetchList',
  async (_, { rejectWithValue }) => {
    try {
      const res = await vanchiService.getVanchiList();
      if (Array.isArray(res)) {
        return { data: res, rowCount: res.length };
      }
      return { data: (res as any).data ?? [], rowCount: (res as any).rowCount ?? ((res as any).data?.length || 0) };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch vanchi list');
    }
  }
);

export const fetchVanchiStats = createAsyncThunk(
  'vanchi/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const stats = await vanchiService.getVanchiStats();
      return stats;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch vanchi stats');
    }
  }
);

export const fetchVanchiByDateRange = createAsyncThunk(
  'vanchi/fetchByDateRange',
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const res = await vanchiService.getVanchiByDateRange(startDate, endDate);
      if (Array.isArray(res)) {
        return { data: res, rowCount: res.length };
      }
      return { data: (res as any).data ?? [], rowCount: (res as any).rowCount ?? ((res as any).data?.length || 0) };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch vanchi by date range');
    }
  }
);

export const fetchReportStats = createAsyncThunk(
  'vanchi/fetchReportStats',
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const stats = await vanchiService.getReportStats(startDate, endDate);
      return stats;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch report stats');
    }
  }
);



// Slice
const vanchiSlice = createSlice({
  name: 'vanchi',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vanchi list
      .addCase(fetchVanchiList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVanchiList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = (action.payload as any).data;
        state.total = (action.payload as any).rowCount;
      })
      .addCase(fetchVanchiList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch vanchi stats
      .addCase(fetchVanchiStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVanchiStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchVanchiStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch vanchi by date range
      .addCase(fetchVanchiByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVanchiByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.list = (action.payload as any).data;
        state.total = (action.payload as any).rowCount;
      })
      .addCase(fetchVanchiByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch report stats
      .addCase(fetchReportStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchReportStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
  },
});

export const { clearError, setLoading } = vanchiSlice.actions;
export default vanchiSlice.reducer;
